import React from 'react';
import { SYSTEM_MODULES } from '../../config/moduleList'; 
import { LockClosedIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';

const RoleMatrix = ({ selectedPermissions, setSelectedPermissions, isReadOnly = false }) => {

  // 1. Toggle a single permission
  const togglePermission = (key) => {
    if (isReadOnly) return;
    
    if (selectedPermissions.includes(key)) {
      // Remove (Disable)
      setSelectedPermissions(prev => prev.filter(p => p !== key));
    } else {
      // Add (Enable)
      setSelectedPermissions(prev => [...prev, key]);
    }
  };

  // 2. Toggle "Select All" for a category
  const toggleCategory = (modules, isAllSelected) => {
    if (isReadOnly) return;
    const keys = modules.map(m => m.key);
    
    if (isAllSelected) {
      // Uncheck all
      setSelectedPermissions(prev => prev.filter(p => !keys.includes(p)));
    } else {
      // Check all
      setSelectedPermissions(prev => [...new Set([...prev, ...keys])]);
    }
  };

  return (
    <div className="space-y-6">
      {SYSTEM_MODULES.map((cat, idx) => {
        // Check status of this category
        const categoryKeys = cat.modules.map(m => m.key);
        const isAllSelected = categoryKeys.every(k => selectedPermissions.includes(k));

        return (
          <div key={idx} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            {/* Category Header */}
            <div className="bg-emerald-50 px-6 py-3 border-b border-emerald-100 flex justify-between items-center">
              <h3 className="font-bold text-emerald-800 flex items-center gap-2">
                {cat.category}
              </h3>
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={() => toggleCategory(cat.modules, isAllSelected)}
                  className={`text-xs font-bold px-3 py-1 rounded-full border transition-all ${
                    isAllSelected 
                      ? 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200' 
                      : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {isAllSelected ? 'Unselect All' : 'Select All'}
                </button>
              )}
            </div>

            {/* Grid of Toggles */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cat.modules.map((mod) => {
                const isEnabled = selectedPermissions.includes(mod.key);
                
                return (
                  <div 
                    key={mod.key}
                    onClick={() => togglePermission(mod.key)}
                    className={`
                      group relative flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all select-none
                      ${isEnabled 
                        ? 'bg-emerald-50/50 border-emerald-500 ring-1 ring-emerald-500' 
                        : 'bg-white border-gray-200 hover:border-emerald-300'
                      }
                      ${isReadOnly ? 'cursor-default opacity-80' : ''}
                    `}
                  >
                    <div className="flex items-center gap-3">
                        <div className={`
                        w-10 h-6 rounded-full p-1 flex items-center transition-colors duration-300
                        ${isEnabled ? 'bg-emerald-500' : 'bg-gray-300'}
                        `}>
                            <div className={`
                                bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300
                                ${isEnabled ? 'translate-x-4' : 'translate-x-0'}
                            `}/>
                        </div>
                        <span className={`text-sm font-medium ${isEnabled ? 'text-emerald-900' : 'text-gray-600'}`}>
                        {mod.label}
                        </span>
                    </div>

                    {isReadOnly && <LockClosedIcon className="w-4 h-4 text-gray-400" />}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RoleMatrix;